/**
 * Mock API Service
 * Replaces real AWS Lambda calls with realistic mock responses
 */

import {
  MockApiConfig,
  MockApiResponse,
  defaultMockConfig,
  simulateDelay,
  shouldSimulateError,
  shouldSimulateFailover,
  createMockResponse,
  MockPayment,
  MockSettlement,
  MockProviderStatus,
  MockExchangeRate,
  MockDashboardStats,
  MockInvoice,
  MockInvoiceFormData
} from './mockApi'

import { getMockDataConfig, getConfig } from '../config'

import {
  generateMockPayment,
  generateMockPayments,
  generateMockSettlement,
  generateMockSettlements,
  generateAllProviderStatuses,
  generateAllProviders,
  generateAllExchangeRates,
  generateMockDashboardStats,
  generateMockChartData
} from './mockDataGenerators'

/**
 * Mock API Service Class
 * Provides realistic API responses with configurable delays and error rates
 */
export class MockApiService {
  private config: MockApiConfig
  private paymentStorage: Map<string, MockPayment> = new Map()
  private settlementStorage: Map<string, MockSettlement> = new Map()
  private providerStatuses: MockProviderStatus[] = []
  private providers: any[] = []
  private exchangeRates: MockExchangeRate[] = []

  constructor(config: MockApiConfig = defaultMockConfig) {
    this.config = this.mergeWithDemoConfig(config)
    this.initializeData()
  }

  /**
   * Merge mock API config with demo configuration
   */
  private mergeWithDemoConfig(config: MockApiConfig): MockApiConfig {
    const demoConfig = getMockDataConfig()
    const appConfig = getConfig()
    
    return {
      ...config,
      baseDelay: appConfig.demo.mockApiDelay || config.baseDelay,
      errorRate: demoConfig.failureRate || config.errorRate,
      failoverRate: demoConfig.simulateFailures ? 0.1 : 0,
      enableRealtimeUpdates: appConfig.demo.enableRealTimeUpdates,
    }
  }

  /**
   * Initialize the service with demo configuration
   */
  async initialize(): Promise<void> {
    const demoConfig = getMockDataConfig()
    
    // Update business profile in mock data
    this.updateBusinessProfile(demoConfig)
    
    console.log('Mock API service initialized with demo configuration:', {
      businessName: demoConfig.businessName,
      businessType: demoConfig.businessType,
      simulateFailures: demoConfig.simulateFailures,
      failureRate: demoConfig.failureRate,
    })
  }

  /**
   * Update business profile based on demo configuration
   */
  private updateBusinessProfile(demoConfig: ReturnType<typeof getMockDataConfig>): void {
    // This would update the mock business profile data
    // Implementation depends on how business profile is stored
  }

  /**
   * Initialize mock data storage
   */
  private initializeData(): void {
    // Generate initial payments
    const initialPayments = generateMockPayments(50)
    initialPayments.forEach(payment => {
      this.paymentStorage.set(payment.id, payment)
    })

    // Generate initial settlements
    const initialSettlements = generateMockSettlements(20)
    initialSettlements.forEach(settlement => {
      this.settlementStorage.set(settlement.id, settlement)
    })

    // Initialize provider statuses
    this.providerStatuses = generateAllProviderStatuses()
    this.providers = generateAllProviders()

    // Initialize exchange rates
    this.exchangeRates = generateAllExchangeRates()

    // Periodically update provider statuses and exchange rates
    this.startPeriodicUpdates()
  }

  /**
   * Start periodic updates for dynamic data
   */
  private startPeriodicUpdates(): void {
    // Update provider statuses every 30 seconds
    setInterval(() => {
      this.providerStatuses = generateAllProviderStatuses()
      this.providers = generateAllProviders()
    }, 30000)

    // Update exchange rates every 10 seconds
    setInterval(() => {
      this.exchangeRates = generateAllExchangeRates()
    }, 10000)
  }

  /**
   * Generic API call wrapper with delay and error simulation
   */
  private async makeApiCall<T>(
    operation: () => T,
    operationName: string = 'API call'
  ): Promise<MockApiResponse<T>> {
    const startTime = Date.now()

    try {
      // Simulate network delay
      await simulateDelay(this.config)

      // Simulate random errors
      if (shouldSimulateError(this.config)) {
        const errorTypes = ['NETWORK_ERROR', 'TIMEOUT', 'SERVER_ERROR', 'RATE_LIMITED'];
        const randomError = errorTypes[Math.floor(Math.random() * errorTypes.length)];
        
        const error = new Error(`Simulated ${randomError} in ${operationName}`);
        (error as any).code = randomError;
        throw error;
      }

      // Execute the operation
      const result = operation()
      const processingTime = Date.now() - startTime

      return createMockResponse(result, processingTime)
    } catch (error) {
      // Add context to error for better handling
      if (error instanceof Error) {
        (error as any).context = { operationName, timestamp: new Date().toISOString() };
      }
      throw error;
    }
  }

  // Authentication APIs
  async login(email: string, password: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => ({
      user: {
        id: 'user_demo123',
        email,
        businessName: 'Demo Business Ltd.',
        businessType: 'SME',
        fpsId: 'FPS123456789',
        kycStatus: 'approved',
        isActive: true
      },
      token: 'mock_jwt_token_' + Math.random().toString(36).substr(2, 9),
      refreshToken: 'mock_refresh_token_' + Math.random().toString(36).substr(2, 9)
    }), 'login')
  }

  async validateToken(token: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => ({
      user: {
        id: 'user_demo123',
        email: 'demo@stablepay.hk',
        businessName: 'Demo Business Ltd.',
        businessType: 'SME',
        fpsId: 'FPS123456789',
        kycStatus: 'approved',
        isActive: true
      }
    }), 'validateToken')
  }

  // Payment APIs
  async getPayments(filters: any = {}): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const payments = Array.from(this.paymentStorage.values())
      const page = filters.page || 1
      const limit = filters.limit || 10
      const startIndex = (page - 1) * limit
      const endIndex = startIndex + limit

      let filteredPayments = payments
      
      if (filters.status) {
        filteredPayments = payments.filter(p => p.status === filters.status)
      }
      
      if (filters.search) {
        filteredPayments = payments.filter(p => 
          p.invoiceNumber.toLowerCase().includes(filters.search.toLowerCase()) ||
          p.id.toLowerCase().includes(filters.search.toLowerCase())
        )
      }

      const paginatedPayments = filteredPayments.slice(startIndex, endIndex)

      return {
        payments: paginatedPayments,
        totalPages: Math.ceil(filteredPayments.length / limit),
        currentPage: page,
        totalCount: filteredPayments.length
      }
    }, 'getPayments')
  }

  async getPayment(id: string): Promise<MockApiResponse<MockPayment | null>> {
    return this.makeApiCall(() => {
      return this.paymentStorage.get(id) || null
    }, 'getPayment')
  }

  async createPayment(paymentData: any): Promise<MockApiResponse<MockPayment>> {
    return this.makeApiCall(() => {
      const payment = generateMockPayment({
        amount: paymentData.amount,
        stablecoinType: paymentData.stablecoinType,
        provider: paymentData.provider,
        settlementRail: paymentData.settlementRail,
        status: 'pending'
      })
      
      this.paymentStorage.set(payment.id, payment)
      return payment
    }, 'createPayment')
  }

  async refreshPaymentStatus(id: string): Promise<MockApiResponse<MockPayment | null>> {
    return this.makeApiCall(() => {
      const payment = this.paymentStorage.get(id)
      if (!payment) return null

      // Simulate status progression
      if (payment.status === 'pending') {
        const shouldConfirm = Math.random() > 0.3 // 70% chance to confirm
        if (shouldConfirm) {
          payment.status = 'confirmed'
          payment.confirmedAt = new Date().toISOString()
          payment.confirmations = Math.floor(Math.random() * 50) + 12
          payment.transactionHash = '0x' + Array.from({ length: 64 }, () => 
            Math.floor(Math.random() * 16).toString(16)
          ).join('')
        }
      }

      this.paymentStorage.set(id, payment)
      return payment
    }, 'refreshPaymentStatus')
  }

  // Settlement APIs
  async getSettlements(filters: any = {}): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const settlements = Array.from(this.settlementStorage.values())
      const page = filters.page || 1
      const limit = filters.limit || 10
      const startIndex = (page - 1) * limit
      const endIndex = startIndex + limit

      let filteredSettlements = settlements
      
      if (filters.status) {
        filteredSettlements = settlements.filter(s => s.status === filters.status)
      }

      const paginatedSettlements = filteredSettlements.slice(startIndex, endIndex)

      return {
        settlements: paginatedSettlements,
        totalPages: Math.ceil(filteredSettlements.length / limit),
        currentPage: page,
        totalCount: filteredSettlements.length
      }
    }, 'getSettlements')
  }

  async getSettlement(id: string): Promise<MockApiResponse<MockSettlement | null>> {
    return this.makeApiCall(() => {
      return this.settlementStorage.get(id) || null
    }, 'getSettlement')
  }

  async createSettlementBatch(): Promise<MockApiResponse<MockSettlement>> {
    return this.makeApiCall(() => {
      const settlement = generateMockSettlement({
        status: 'pending'
      })
      
      this.settlementStorage.set(settlement.id, settlement)
      return settlement
    }, 'createSettlementBatch')
  }

  // Provider Status APIs
  async getProviderStatuses(): Promise<MockApiResponse<MockProviderStatus[]>> {
    return this.makeApiCall(() => {
      // Simulate provider failover scenarios
      if (shouldSimulateFailover(this.config)) {
        const failedProvider = this.providerStatuses[Math.floor(Math.random() * this.providerStatuses.length)]
        failedProvider.status = 'down'
        failedProvider.responseTime = 0
        failedProvider.uptime = Math.random() * 90
      }

      return [...this.providerStatuses]
    }, 'getProviderStatuses')
  }

  async getProviderStatus(provider: string): Promise<MockApiResponse<MockProviderStatus | null>> {
    return this.makeApiCall(() => {
      return this.providerStatuses.find(p => p.provider === provider) || null
    }, 'getProviderStatus')
  }

  // Provider Health Check APIs
  async performHealthCheck(providerId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const provider = this.providerStatuses.find(p => p.provider === providerId)
      if (!provider) {
        throw new Error(`Provider ${providerId} not found`)
      }

      const responseTime = provider.status === 'down' ? 0 : 
                          provider.status === 'degraded' ? 800 + Math.random() * 500 :
                          100 + Math.random() * 200

      return {
        provider: providerId,
        timestamp: new Date().toISOString(),
        responseTime,
        status: provider.status,
        errorMessage: provider.status === 'down' ? 'Provider unreachable' : undefined
      }
    }, 'performHealthCheck')
  }

  // Provider Failover APIs
  async getFailoverHistory(limit: number = 10): Promise<MockApiResponse<any[]>> {
    return this.makeApiCall(() => {
      // Generate mock failover history
      const history = []
      const providers = ['HashKey', 'OSL', 'Moonpay']
      
      for (let i = 0; i < Math.min(limit, 5); i++) {
        const fromProvider = providers[Math.floor(Math.random() * providers.length)]
        let toProvider = providers[Math.floor(Math.random() * providers.length)]
        while (toProvider === fromProvider) {
          toProvider = providers[Math.floor(Math.random() * providers.length)]
        }

        history.push({
          id: `failover_${Math.random().toString(36).substr(2, 9)}`,
          fromProvider,
          toProvider,
          reason: 'Automatic failover due to provider unavailability',
          timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          affectedTransactions: Math.floor(Math.random() * 50) + 10,
          recoveryTime: Math.floor(Math.random() * 30000) + 5000
        })
      }

      return history.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    }, 'getFailoverHistory')
  }

  async forceFailover(fromProvider: string, reason: string = 'Manual failover'): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const providers = ['HashKey', 'OSL', 'Moonpay']
      let toProvider = providers.find(p => p !== fromProvider) || 'OSL'

      // Update provider status
      const provider = this.providerStatuses.find(p => p.provider === fromProvider)
      if (provider) {
        provider.status = 'down'
        provider.responseTime = 0
        provider.uptime = Math.random() * 90
      }

      const failoverEvent = {
        id: `failover_${Math.random().toString(36).substr(2, 9)}`,
        fromProvider,
        toProvider,
        reason,
        timestamp: new Date().toISOString(),
        affectedTransactions: Math.floor(Math.random() * 50) + 10,
        recoveryTime: Math.floor(Math.random() * 30000) + 5000
      }

      return failoverEvent
    }, 'forceFailover')
  }

  async resetProviderStatus(providerId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const provider = this.providerStatuses.find(p => p.provider === providerId)
      if (!provider) {
        throw new Error(`Provider ${providerId} not found`)
      }

      provider.status = 'healthy'
      provider.responseTime = 150 + Math.random() * 100
      provider.uptime = 99.5 + Math.random() * 0.5
      provider.lastUpdate = new Date().toISOString()

      return provider
    }, 'resetProviderStatus')
  }

  // Provider Metrics APIs
  async getProviderMetrics(providerId: string, period: string = '24h'): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const provider = this.providerStatuses.find(p => p.provider === providerId)
      if (!provider) {
        throw new Error(`Provider ${providerId} not found`)
      }

      // Generate mock metrics
      const hours = period === '24h' ? 24 : period === '7d' ? 168 : 24
      const metrics = []
      
      for (let i = hours - 1; i >= 0; i--) {
        const timestamp = new Date(Date.now() - i * 60 * 60 * 1000)
        metrics.push({
          timestamp: timestamp.toISOString(),
          responseTime: provider.status === 'healthy' ? 100 + Math.random() * 200 : 
                       provider.status === 'degraded' ? 500 + Math.random() * 800 : 0,
          uptime: provider.status === 'healthy' ? 99 + Math.random() : 
                 provider.status === 'degraded' ? 95 + Math.random() * 4 : 0,
          throughput: Math.floor(Math.random() * 100) + 50,
          errorRate: provider.status === 'healthy' ? Math.random() * 0.01 : 
                    provider.status === 'degraded' ? Math.random() * 0.05 : 0.1
        })
      }

      return {
        provider: providerId,
        period,
        metrics,
        summary: {
          avgResponseTime: metrics.reduce((sum, m) => sum + m.responseTime, 0) / metrics.length,
          avgUptime: metrics.reduce((sum, m) => sum + m.uptime, 0) / metrics.length,
          totalThroughput: metrics.reduce((sum, m) => sum + m.throughput, 0),
          avgErrorRate: metrics.reduce((sum, m) => sum + m.errorRate, 0) / metrics.length
        }
      }
    }, 'getProviderMetrics')
  }

  // Provider Recommendation API
  async getRecommendedProvider(stablecoin: string, amount: number, settlementRail: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const healthyProviders = this.providerStatuses.filter(p => p.status === 'healthy')
      
      if (healthyProviders.length === 0) {
        return {
          provider: 'OSL', // Fallback
          reason: 'Fallback provider - all others unavailable',
          confidence: 0.3
        }
      }

      // Simple recommendation logic based on provider capabilities
      let recommendedProvider = healthyProviders[0]
      let reason = 'Best available provider'
      let confidence = 0.8

      // Prefer HK-regulated providers for HKD₮ and CNH₮
      if (stablecoin === 'HKD₮' || stablecoin === 'CNH₮') {
        const hkProviders = healthyProviders.filter(p => p.provider === 'HashKey' || p.provider === 'OSL')
        if (hkProviders.length > 0) {
          recommendedProvider = hkProviders[0]
          reason = 'HK-regulated provider for HK stablecoins'
          confidence = 0.95
        }
      }

      // Prefer providers with better response times for large amounts
      if (amount > 100000) {
        const fastProviders = healthyProviders.filter(p => p.responseTime < 300)
        if (fastProviders.length > 0) {
          recommendedProvider = fastProviders[0]
          reason = 'Optimized for large transactions'
          confidence = 0.9
        }
      }

      return {
        provider: recommendedProvider.provider,
        reason,
        confidence
      }
    }, 'getRecommendedProvider')
  }

  // Exchange Rate APIs
  async getExchangeRates(): Promise<MockApiResponse<MockExchangeRate[]>> {
    return this.makeApiCall(() => {
      return [...this.exchangeRates]
    }, 'getExchangeRates')
  }

  async getExchangeRate(stablecoin: string): Promise<MockApiResponse<MockExchangeRate | null>> {
    return this.makeApiCall(() => {
      return this.exchangeRates.find(r => r.stablecoin === stablecoin) || null
    }, 'getExchangeRate')
  }

  // Dashboard APIs
  async getDashboardStats(): Promise<MockApiResponse<MockDashboardStats>> {
    return this.makeApiCall(() => {
      return generateMockDashboardStats()
    }, 'getDashboardStats')
  }

  // Settlement Analytics APIs
  async getSettlementAnalytics(period: string = '30d'): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const rails = ['FPS', 'CIPS', 'e-CNY']
      const analytics = rails.map(rail => ({
        rail,
        name: rail === 'FPS' ? 'Faster Payment System' :
              rail === 'CIPS' ? 'Cross-border Interbank Payment System' :
              'Digital Yuan',
        averageTime: rail === 'FPS' ? 12.5 : rail === 'CIPS' ? 4.2 * 60 : 0.8, // minutes
        successRate: rail === 'FPS' ? 98.5 : rail === 'CIPS' ? 96.8 : 99.2, // percentage
        volume: rail === 'FPS' ? 125000 : rail === 'CIPS' ? 89000 : 45000, // HKD
        transactionCount: rail === 'FPS' ? 45 : rail === 'CIPS' ? 23 : 18,
        averageFee: rail === 'FPS' ? 0.1 : rail === 'CIPS' ? 0.2 : 0.05, // percentage
        status: 'operational'
      }))

      const summary = {
        totalVolume: analytics.reduce((sum, rail) => sum + rail.volume, 0),
        totalTransactions: analytics.reduce((sum, rail) => sum + rail.transactionCount, 0),
        averageSettlementTime: analytics.reduce((sum, rail) => sum + rail.averageTime, 0) / analytics.length,
        overallSuccessRate: analytics.reduce((sum, rail) => sum + rail.successRate, 0) / analytics.length,
        feesSaved: 8500, // HKD saved vs traditional banking
        speedImprovement: 85 // percentage faster than traditional
      }

      return {
        rails: analytics,
        summary,
        period
      }
    }, 'getSettlementAnalytics')
  }

  async getSettlementHistory(filters: any = {}): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const settlements = []
      const rails = ['FPS', 'CIPS', 'e-CNY']
      
      for (let i = 0; i < 20; i++) {
        const rail = rails[Math.floor(Math.random() * rails.length)]
        const amount = Math.floor(Math.random() * 50000) + 5000
        const createdAt = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
        const settlementTime = rail === 'FPS' ? 15 : rail === 'CIPS' ? 240 : 1 // minutes
        const settledAt = new Date(createdAt.getTime() + settlementTime * 60 * 1000)
        
        settlements.push({
          id: `settlement_${Math.random().toString(36).substr(2, 9)}`,
          paymentId: `payment_${Math.random().toString(36).substr(2, 9)}`,
          rail,
          amount,
          currency: 'HKD',
          status: Math.random() > 0.1 ? 'completed' : 'processing',
          createdAt: createdAt.toISOString(),
          settledAt: settledAt.toISOString(),
          actualSettlementTime: settlementTime,
          businessAccount: rail === 'FPS' ? 'HSBC-HK-***6789' :
                          rail === 'CIPS' ? 'BOC-CN-***4321' :
                          'eCNY-WALLET-***6789',
          fees: amount * (rail === 'FPS' ? 0.001 : rail === 'CIPS' ? 0.002 : 0.0005)
        })
      }

      return settlements.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    }, 'getSettlementHistory')
  }

  async getDashboardChartData(period: string = '7d'): Promise<MockApiResponse<any[]>> {
    return this.makeApiCall(() => {
      const days = period === '30d' ? 30 : period === '7d' ? 7 : 1
      return generateMockChartData(days)
    }, 'getDashboardChartData')
  }

  // Cost Calculator APIs
  async calculateCosts(amount: number, stablecoin: string, settlementRail: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Import calculator service dynamically to avoid circular dependencies
      const { calculatorService } = require('./calculatorService')
      
      try {
        const result = calculatorService.calculateSavings({
          amount,
          stablecoin,
          settlementRail
        })

        return result
      } catch (error) {
        console.error('Calculator service error:', error)
        
        // Fallback to original calculation if calculator service fails
        const exchangeRate = this.exchangeRates.find(r => r.stablecoin === stablecoin)?.rate || 7.8
        const spread = this.exchangeRates.find(r => r.stablecoin === stablecoin)?.spread || 0.003
        
        const stablecoinAmount = amount / exchangeRate
        const fxCost = amount * spread
        
        const processingFees = {
          'FPS': amount * 0.001, // 0.1%
          'CIPS': amount * 0.002, // 0.2%
          'e-CNY': amount * 0.0005 // 0.05%
        }
        
        const processingFee = processingFees[settlementRail as keyof typeof processingFees] || processingFees.FPS
        const totalCost = fxCost + processingFee
        
        // Traditional SWIFT comparison
        const traditionalCost = amount * 0.035 // 3.5% typical SWIFT cost
        const savings = traditionalCost - totalCost
        const savingsPercentage = (savings / traditionalCost) * 100

        return {
          amount,
          stablecoinAmount: Math.round(stablecoinAmount * 1000000) / 1000000,
          exchangeRate,
          fxCost: Math.round(fxCost * 100) / 100,
          processingFee: Math.round(processingFee * 100) / 100,
          totalCost: Math.round(totalCost * 100) / 100,
          traditionalCost: Math.round(traditionalCost * 100) / 100,
          savings: Math.round(savings * 100) / 100,
          savingsPercentage: Math.round(savingsPercentage * 10) / 10,
          settlementRail,
          estimatedTime: {
            'FPS': '15 minutes',
            'CIPS': 'Same day',
            'e-CNY': 'Instant'
          }[settlementRail] || '15 minutes'
        }
      }
    }, 'calculateCosts')
  }

  // On-Ramp APIs
  async getOnRampProviders(): Promise<MockApiResponse<any[]>> {
    return this.makeApiCall(() => {
      return [
        {
          id: 'moonpay',
          name: 'Moonpay',
          logo: '/logos/moonpay.png',
          supportedStablecoins: ['USDT', 'USDC', 'HKD₮', 'CNH₮'],
          supportedCountries: ['US', 'UK', 'EU', 'CA', 'AU', 'SG', 'HK'],
          fees: {
            percentage: 0.045, // 4.5%
            fixed: 5
          },
          estimatedTime: '10-15 minutes',
          isAvailable: true
        },
        {
          id: 'hashkey-exchange',
          name: 'HashKey Exchange',
          logo: '/logos/hashkey.png',
          supportedStablecoins: ['USDT', 'USDC', 'HKD₮', 'CNH₮'],
          supportedCountries: ['HK', 'SG'],
          fees: {
            percentage: 0.025, // 2.5%
            fixed: 2
          },
          estimatedTime: '5-10 minutes',
          isAvailable: true
        },
        {
          id: 'osl-exchange',
          name: 'OSL Exchange',
          logo: '/logos/osl.png',
          supportedStablecoins: ['USDT', 'USDC', 'HKD₮'],
          supportedCountries: ['HK', 'SG', 'JP'],
          fees: {
            percentage: 0.03, // 3%
            fixed: 3
          },
          estimatedTime: '5-10 minutes',
          isAvailable: true
        }
      ]
    }, 'getOnRampProviders')
  }

  async getOnRampQuote(quoteData: any): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const { provider, stablecoin, amount, currency } = quoteData
      
      // Get exchange rate for stablecoin
      const exchangeRate = this.exchangeRates.find(r => r.stablecoin === stablecoin)?.rate || 7.8
      const stablecoinAmount = amount / exchangeRate
      
      // Get provider fees
      const providerFees = {
        'Moonpay': { percentage: 0.045, fixed: 5 },
        'HashKey Exchange': { percentage: 0.025, fixed: 2 },
        'OSL Exchange': { percentage: 0.03, fixed: 3 }
      }
      
      const fees = providerFees[provider as keyof typeof providerFees] || providerFees['Moonpay']
      const feeAmount = amount * fees.percentage + fees.fixed
      const totalCost = amount + feeAmount
      
      return {
        provider,
        stablecoin,
        amount: stablecoinAmount,
        fiatAmount: amount,
        fees: feeAmount,
        totalCost,
        estimatedTime: provider === 'Moonpay' ? '10-15 minutes' : '5-10 minutes',
        exchangeRate
      }
    }, 'getOnRampQuote')
  }

  async initiateOnRampPurchase(purchaseData: any): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Simulate on-ramp purchase initiation
      const purchaseId = `onramp_${Math.random().toString(36).substr(2, 9)}`
      
      return {
        id: purchaseId,
        provider: purchaseData.provider,
        stablecoin: purchaseData.stablecoin,
        amount: purchaseData.amount,
        fiatAmount: purchaseData.fiatAmount,
        totalCost: purchaseData.totalCost,
        status: 'pending',
        paymentUrl: `https://${purchaseData.provider.toLowerCase()}.com/purchase/${purchaseId}`,
        estimatedTime: purchaseData.estimatedTime,
        createdAt: new Date().toISOString()
      }
    }, 'initiateOnRampPurchase')
  }

  // Network Fee Calculator API
  async calculateNetworkFees(network: string, priority: string, gasLimit?: number): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const { calculatorService } = require('./calculatorService')
      
      try {
        const result = calculatorService.calculateNetworkFees({
          network: network as 'ethereum' | 'polygon' | 'tron',
          priority: priority as 'low' | 'medium' | 'high',
          gasLimit
        })

        return result
      } catch (error) {
        console.error('Network fee calculation error:', error)
        throw error
      }
    }, 'calculateNetworkFees')
  }

  // Exchange Rate Calculator API
  async calculateExchangeRate(fromCurrency: string, toCurrency: string, amount: number): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const { calculatorService } = require('./calculatorService')
      
      try {
        const result = calculatorService.calculateExchangeRate({
          fromCurrency,
          toCurrency,
          amount
        })

        return result
      } catch (error) {
        console.error('Exchange rate calculation error:', error)
        throw error
      }
    }, 'calculateExchangeRate')
  }

  // Settlement Time Estimator API
  async estimateSettlementTime(rail: string, amount: number, currency: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const { calculatorService } = require('./calculatorService')
      
      try {
        const result = calculatorService.estimateSettlementTime({
          rail: rail as 'FPS' | 'CIPS' | 'e-CNY' | 'SWIFT',
          amount,
          currency
        })

        return result
      } catch (error) {
        console.error('Settlement time estimation error:', error)
        throw error
      }
    }, 'estimateSettlementTime')
  }

  async getOnRampPurchaseStatus(purchaseId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Simulate purchase status progression
      const statuses = ['pending', 'processing', 'completed', 'failed']
      const randomStatus = statuses[Math.floor(Math.random() * statuses.length)]
      
      return {
        id: purchaseId,
        status: randomStatus,
        progress: randomStatus === 'pending' ? 25 : 
                 randomStatus === 'processing' ? 75 : 
                 randomStatus === 'completed' ? 100 : 0,
        estimatedTimeRemaining: randomStatus === 'completed' ? '0 minutes' : 
                               randomStatus === 'processing' ? '2-5 minutes' : 
                               '5-10 minutes',
        transactionHash: randomStatus === 'completed' ? 
          '0x' + Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('') : 
          undefined
      }
    }, 'getOnRampPurchaseStatus')
  }

  // Wallet Connection APIs
  async connectWallet(walletProvider: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Simulate wallet connection
      const mockAddress = `0x${Math.random().toString(16).substr(2, 40)}`
      
      return {
        address: mockAddress,
        provider: walletProvider,
        network: 'Ethereum Mainnet',
        isConnected: true,
        connectionTime: new Date().toISOString()
      }
    }, 'connectWallet')
  }

  async getWalletBalances(address: string): Promise<MockApiResponse<any[]>> {
    return this.makeApiCall(() => {
      // Generate mock wallet balances
      const balances = [
        {
          stablecoin: 'USDT',
          balance: Math.random() * 1000 + 100,
          formattedBalance: '',
          isLoading: false,
          contractAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7'
        },
        {
          stablecoin: 'USDC',
          balance: Math.random() * 500 + 50,
          formattedBalance: '',
          isLoading: false,
          contractAddress: '0xA0b86a33E6441b8435b662303c0f4c8c8c8c8c8c'
        },
        {
          stablecoin: 'HKD₮',
          balance: Math.random() * 10000 + 1000,
          formattedBalance: '',
          isLoading: false,
          contractAddress: '0x1234567890123456789012345678901234567890'
        },
        {
          stablecoin: 'CNH₮',
          balance: Math.random() * 5000 + 500,
          formattedBalance: '',
          isLoading: false,
          contractAddress: '0x0987654321098765432109876543210987654321'
        }
      ]

      // Format balances
      balances.forEach(balance => {
        balance.formattedBalance = balance.balance.toFixed(6)
      })

      return balances
    }, 'getWalletBalances')
  }

  async verifyWalletBalance(address: string, stablecoin: string, requiredAmount: number): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Get wallet balance for specific stablecoin
      const mockBalance = Math.random() * 1000 + 100
      const hasSufficientBalance = mockBalance >= requiredAmount
      
      return {
        address,
        stablecoin,
        balance: mockBalance,
        formattedBalance: mockBalance.toFixed(6),
        requiredAmount,
        hasSufficientBalance,
        shortfall: hasSufficientBalance ? 0 : requiredAmount - mockBalance
      }
    }, 'verifyWalletBalance')
  }

  async initiateWalletPayment(paymentData: any): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Simulate wallet payment initiation
      const transactionHash = '0x' + Array.from({ length: 64 }, () => 
        Math.floor(Math.random() * 16).toString(16)
      ).join('')
      
      const payment = {
        id: `payment_${Math.random().toString(36).substr(2, 9)}`,
        transactionHash,
        status: 'pending',
        amount: paymentData.amount,
        stablecoin: paymentData.stablecoin,
        fromAddress: paymentData.fromAddress,
        toAddress: paymentData.toAddress,
        networkFee: Math.random() * 0.01 + 0.005, // Random network fee
        confirmations: 0,
        requiredConfirmations: 12,
        estimatedConfirmationTime: '2-5 minutes',
        createdAt: new Date().toISOString()
      }
      
      // Store payment for status tracking
      this.paymentStorage.set(payment.id, payment as any)
      
      return payment
    }, 'initiateWalletPayment')
  }

  async getWalletPaymentStatus(paymentId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const payment = this.paymentStorage.get(paymentId)
      if (!payment) {
        throw new Error('Payment not found')
      }

      // Simulate confirmation progress
      if (payment.status === 'pending') {
        const shouldProgress = Math.random() > 0.3 // 70% chance to progress
        if (shouldProgress) {
          payment.confirmations = Math.min(payment.confirmations + Math.floor(Math.random() * 3) + 1, 12)
          
          if (payment.confirmations >= 12) {
            payment.status = 'confirmed'
            payment.confirmedAt = new Date().toISOString()
          }
        }
      }

      return {
        id: payment.id,
        transactionHash: payment.transactionHash,
        status: payment.status,
        confirmations: payment.confirmations,
        requiredConfirmations: 12,
        estimatedTime: payment.confirmations < 12 ? `${Math.max(1, 5 - payment.confirmations)} minutes` : 'Confirmed',
        blockchainExplorerUrl: `https://etherscan.io/tx/${payment.transactionHash}`
      }
    }, 'getWalletPaymentStatus')
  }

  // Customer Payment Flow APIs
  
  /**
   * Get invoice details for customer payment page
   * Used when customer clicks on payment link
   */
  async getInvoiceForPayment(invoiceId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Generate mock invoice for customer payment
      const invoice = {
        id: invoiceId,
        invoiceNumber: `INV-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
        businessName: 'Demo Business Ltd.',
        businessLogo: '/logos/demo-business.png',
        businessEmail: 'payments@demobusiness.hk',
        businessAddress: 'Central, Hong Kong',
        amount: Math.floor(Math.random() * 50000) + 1000, // HKD 1,000 - 51,000
        currency: 'HKD',
        description: 'Professional services invoice',
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
        status: 'sent',
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
        customerName: 'John Smith',
        customerEmail: 'john.smith@example.com',
        customerCountry: 'United States',
        settlementRail: 'FPS',
        paymentInstructions: 'Please pay using the stablecoin options below. Payment will be confirmed within 15 minutes.',
        isExpired: false,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      }
      
      return invoice
    }, 'getInvoiceForPayment')
  }

  /**
   * Get available payment options for customer
   * Shows stablecoins, exchange rates, and costs
   */
  async getCustomerPaymentOptions(invoiceId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const invoice = {
        amount: Math.floor(Math.random() * 50000) + 1000,
        currency: 'HKD'
      }

      const paymentOptions = this.exchangeRates.map(rate => {
        const stablecoinAmount = invoice.amount / rate.rate
        const networkFee = Math.random() * 0.01 + 0.005 // Random network fee
        const processingFee = invoice.amount * 0.001 // 0.1% processing fee
        const totalCost = stablecoinAmount + networkFee + (processingFee / rate.rate)

        return {
          stablecoin: rate.stablecoin,
          exchangeRate: rate.rate,
          spread: rate.spread,
          stablecoinAmount: Math.round(stablecoinAmount * 1000000) / 1000000,
          networkFee: Math.round(networkFee * 1000000) / 1000000,
          processingFee: Math.round(processingFee * 100) / 100,
          totalCost: Math.round(totalCost * 1000000) / 1000000,
          isAvailable: true,
          estimatedTime: '2-5 minutes',
          provider: rate.stablecoin === 'HKD₮' || rate.stablecoin === 'CNH₮' ? 'HashKey' : 'OSL'
        }
      })

      return {
        invoiceId,
        invoiceAmount: invoice.amount,
        invoiceCurrency: invoice.currency,
        paymentOptions,
        onRampAvailable: true,
        supportedWallets: ['MetaMask', 'WalletConnect', 'Coinbase Wallet']
      }
    }, 'getCustomerPaymentOptions')
  }

  /**
   * Calculate customer payment costs with comparison to traditional methods
   */
  async calculateCustomerPaymentCost(invoiceAmount: number, stablecoin: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Import calculator service dynamically to avoid circular dependencies
      const { calculatorService } = require('./calculatorService')
      
      try {
        const result = calculatorService.calculateSavings({
          amount: invoiceAmount,
          stablecoin,
          settlementRail: 'FPS' // Default to FPS for customer payments
        })

        // Transform to customer payment format
        const networkFee = Math.random() * 0.01 + 0.005 // Random network fee in stablecoin
        const networkFeeHKD = networkFee * result.exchangeRate

        return {
          invoiceAmount,
          stablecoin,
          exchangeRate: result.exchangeRate,
          stablecoinAmount: result.stablecoinAmount,
          breakdown: {
            fxCost: result.fxCost,
            networkFee: Math.round(networkFee * 1000000) / 1000000,
            processingFee: result.processingFee,
            totalFees: Math.round((result.totalCost + networkFeeHKD) * 100) / 100
          },
          comparison: {
            traditionalFees: result.traditionalCost,
            traditionalTime: '3-5 business days',
            stablecoinTime: '2-5 minutes',
            savings: Math.round((result.savings - networkFeeHKD) * 100) / 100,
            savingsPercentage: Math.round(((result.savings - networkFeeHKD) / result.traditionalCost) * 100 * 10) / 10,
            timeAdvantage: '99% faster than traditional transfers'
          },
          estimatedConfirmationTime: '2-5 minutes'
        }
      } catch (error) {
        console.error('Calculator service error:', error)
        
        // Fallback to original calculation
        const exchangeRate = this.exchangeRates.find(r => r.stablecoin === stablecoin)?.rate || 7.8
        const spread = this.exchangeRates.find(r => r.stablecoin === stablecoin)?.spread || 0.003
        
        const stablecoinAmount = invoiceAmount / exchangeRate
        const fxCost = invoiceAmount * spread
        const networkFee = Math.random() * 0.01 + 0.005 // Random network fee in stablecoin
        const processingFee = invoiceAmount * 0.001 // 0.1% processing fee
        const totalFees = fxCost + processingFee + (networkFee * exchangeRate)
        
        // Traditional remittance comparison
        const traditionalFees = invoiceAmount * 0.035 // 3.5% typical international transfer
        const traditionalTime = '3-5 business days'
        const stablecoinTime = '2-5 minutes'
        
        const savings = traditionalFees - totalFees
        const savingsPercentage = (savings / traditionalFees) * 100
        const timeAdvantage = '99% faster than traditional transfers'

        return {
          invoiceAmount,
          stablecoin,
          exchangeRate,
          stablecoinAmount: Math.round(stablecoinAmount * 1000000) / 1000000,
          breakdown: {
            fxCost: Math.round(fxCost * 100) / 100,
            networkFee: Math.round(networkFee * 1000000) / 1000000,
            processingFee: Math.round(processingFee * 100) / 100,
            totalFees: Math.round(totalFees * 100) / 100
          },
          comparison: {
            traditionalFees: Math.round(traditionalFees * 100) / 100,
            traditionalTime,
            stablecoinTime,
            savings: Math.round(savings * 100) / 100,
            savingsPercentage: Math.round(savingsPercentage * 10) / 10,
            timeAdvantage
          },
          estimatedConfirmationTime: stablecoinTime
        }
      }
    }, 'calculateCustomerPaymentCost')
  }

  /**
   * Get customer-specific exchange rates with real-time updates
   */
  async getCustomerExchangeRates(): Promise<MockApiResponse<any[]>> {
    return this.makeApiCall(() => {
      return this.exchangeRates.map(rate => ({
        ...rate,
        customerRate: rate.rate * (1 + rate.spread), // Customer pays slightly more
        trend: Math.random() > 0.5 ? 'up' : 'down',
        change24h: (Math.random() - 0.5) * 0.02, // ±1% daily change
        isRecommended: rate.stablecoin === 'HKD₮' || rate.stablecoin === 'USDC',
        availability: 'high'
      }))
    }, 'getCustomerExchangeRates')
  }

  /**
   * Initiate customer payment process
   */
  async initiateCustomerPayment(paymentData: any): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const paymentId = `cust_pay_${Math.random().toString(36).substr(2, 9)}`
      
      const payment = {
        id: paymentId,
        invoiceId: paymentData.invoiceId,
        amount: paymentData.amount,
        stablecoin: paymentData.stablecoin,
        stablecoinAmount: paymentData.stablecoinAmount,
        paymentMethod: paymentData.paymentMethod, // 'wallet' or 'on_ramp'
        customerAddress: paymentData.customerAddress,
        businessAddress: paymentData.businessAddress,
        status: 'initiated',
        transactionHash: null,
        confirmations: 0,
        requiredConfirmations: 12,
        estimatedTime: '2-5 minutes',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutes
      }

      // Store payment for tracking
      this.paymentStorage.set(paymentId, payment as any)

      return payment
    }, 'initiateCustomerPayment')
  }

  /**
   * Get customer payment status with detailed progress
   */
  async getCustomerPaymentStatus(paymentId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const payment = this.paymentStorage.get(paymentId)
      if (!payment) {
        throw new Error('Payment not found')
      }

      // Simulate payment progression
      if (payment.status === 'initiated' || payment.status === 'pending') {
        const shouldProgress = Math.random() > 0.2 // 80% chance to progress
        if (shouldProgress) {
          if (!payment.transactionHash) {
            payment.transactionHash = '0x' + Array.from({ length: 64 }, () => 
              Math.floor(Math.random() * 16).toString(16)
            ).join('')
            payment.status = 'pending'
          } else {
            payment.confirmations = Math.min(payment.confirmations + Math.floor(Math.random() * 3) + 1, 12)
            
            if (payment.confirmations >= 12) {
              payment.status = 'confirmed'
              payment.confirmedAt = new Date().toISOString()
            }
          }
        }
      }

      const progressPercentage = payment.status === 'confirmed' ? 100 :
                                payment.status === 'pending' ? Math.min(90, (payment.confirmations / 12) * 90) :
                                payment.status === 'initiated' ? 10 : 0

      return {
        id: payment.id,
        status: payment.status,
        transactionHash: payment.transactionHash,
        confirmations: payment.confirmations,
        requiredConfirmations: 12,
        progressPercentage,
        estimatedTimeRemaining: payment.status === 'confirmed' ? '0 minutes' :
                                payment.confirmations > 6 ? '1-2 minutes' :
                                payment.confirmations > 0 ? '3-5 minutes' : '5-10 minutes',
        blockchainExplorerUrl: payment.transactionHash ? 
          `https://etherscan.io/tx/${payment.transactionHash}` : null,
        receipt: payment.status === 'confirmed' ? {
          paymentId: payment.id,
          transactionHash: payment.transactionHash,
          amount: payment.amount,
          stablecoin: payment.stablecoin,
          timestamp: payment.confirmedAt,
          businessName: 'Demo Business Ltd.',
          invoiceNumber: `INV-${Math.random().toString(36).substr(2, 8).toUpperCase()}`
        } : null
      }
    }, 'getCustomerPaymentStatus')
  }

  /**
   * Generate customer payment receipt
   */
  async generateCustomerReceipt(paymentId: string): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const payment = this.paymentStorage.get(paymentId)
      if (!payment || payment.status !== 'confirmed') {
        throw new Error('Payment not found or not confirmed')
      }

      const receipt = {
        receiptId: `receipt_${Math.random().toString(36).substr(2, 9)}`,
        paymentId: payment.id,
        invoiceNumber: `INV-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
        businessName: 'Demo Business Ltd.',
        businessAddress: 'Central, Hong Kong',
        customerName: 'John Smith',
        customerEmail: 'john.smith@example.com',
        amount: payment.amount,
        stablecoin: payment.stablecoin,
        stablecoinAmount: payment.stablecoinAmount,
        exchangeRate: payment.exchangeRate || 7.8,
        transactionHash: payment.transactionHash,
        confirmationTime: payment.confirmedAt,
        settlementRail: 'FPS',
        estimatedSettlement: '15 minutes',
        fees: {
          networkFee: Math.random() * 0.01 + 0.005,
          processingFee: payment.amount * 0.001,
          total: (Math.random() * 0.01 + 0.005) + (payment.amount * 0.001)
        },
        downloadUrl: `/api/receipts/${payment.id}/download`,
        createdAt: new Date().toISOString()
      }

      return receipt
    }, 'generateCustomerReceipt')
  }

  /**
   * Enhanced wallet connection with balance verification
   */
  async connectCustomerWallet(walletProvider: string, requiredStablecoin: string, requiredAmount: number): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      const mockAddress = `0x${Math.random().toString(16).substr(2, 40)}`
      
      // Generate realistic balances
      const balances = [
        {
          stablecoin: 'USDT',
          balance: Math.random() * 1000 + 100,
          contractAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
          decimals: 6
        },
        {
          stablecoin: 'USDC',
          balance: Math.random() * 500 + 50,
          contractAddress: '0xA0b86a33E6441b8435b662303c0f4c8c8c8c8c8c',
          decimals: 6
        },
        {
          stablecoin: 'HKD₮',
          balance: Math.random() * 10000 + 1000,
          contractAddress: '0x1234567890123456789012345678901234567890',
          decimals: 18
        },
        {
          stablecoin: 'CNH₮',
          balance: Math.random() * 5000 + 500,
          contractAddress: '0x0987654321098765432109876543210987654321',
          decimals: 18
        }
      ]

      const requiredBalance = balances.find(b => b.stablecoin === requiredStablecoin)
      const hasSufficientBalance = requiredBalance ? requiredBalance.balance >= requiredAmount : false

      return {
        address: mockAddress,
        provider: walletProvider,
        network: 'Ethereum Mainnet',
        isConnected: true,
        balances: balances.map(b => ({
          ...b,
          formattedBalance: b.balance.toFixed(6),
          isLoading: false
        })),
        verification: {
          requiredStablecoin,
          requiredAmount,
          availableBalance: requiredBalance?.balance || 0,
          hasSufficientBalance,
          shortfall: hasSufficientBalance ? 0 : requiredAmount - (requiredBalance?.balance || 0)
        },
        connectionTime: new Date().toISOString()
      }
    }, 'connectCustomerWallet')
  }

  /**
   * Simulate wallet transaction signing
   */
  async signWalletTransaction(transactionData: any): Promise<MockApiResponse<any>> {
    return this.makeApiCall(() => {
      // Simulate transaction signing delay
      const transactionHash = '0x' + Array.from({ length: 64 }, () => 
        Math.floor(Math.random() * 16).toString(16)
      ).join('')

      return {
        transactionHash,
        status: 'signed',
        gasUsed: Math.floor(Math.random() * 50000) + 21000,
        gasPrice: Math.floor(Math.random() * 20) + 10, // Gwei
        blockNumber: Math.floor(Math.random() * 1000000) + 18000000,
        timestamp: new Date().toISOString(),
        confirmations: 0,
        networkFee: Math.random() * 0.01 + 0.005
      }
    }, 'signWalletTransaction')
  }

  // Invoice Management APIs
  async getInvoices(filters?: { status?: string; limit?: number; page?: number }): Promise<MockApiResponse<{ invoices: MockInvoice[]; total: number; page: number; limit: number }>> {
    return this.makeApiCall(() => {
      // Generate mock invoices
      const mockInvoices: MockInvoice[] = []
      const totalInvoices = 25
      
      for (let i = 0; i < totalInvoices; i++) {
        const createdDate = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)
        const dueDate = new Date(createdDate.getTime() + 14 * 24 * 60 * 60 * 1000)
        const invoiceNumber = `INV-${String(i + 1).padStart(4, '0')}`
        const id = `invoice_${Math.random().toString(36).substr(2, 9)}`
        
        const statuses = ['draft', 'sent', 'paid', 'overdue'] as const
        const status = statuses[Math.floor(Math.random() * statuses.length)]
        
        const currencies = ['HKD', 'CNY', 'USD'] as const
        const currency = currencies[Math.floor(Math.random() * currencies.length)]
        
        const settlementRails = ['FPS', 'CIPS', 'e-CNY'] as const
        const settlementRail = settlementRails[Math.floor(Math.random() * settlementRails.length)]
        
        const countries = ['United States', 'United Kingdom', 'Singapore', 'Japan', 'Australia', 'Canada', 'Germany', 'France']
        const customerCountry = countries[Math.floor(Math.random() * countries.length)]
        
        mockInvoices.push({
          id,
          invoiceNumber,
          businessName: 'Hong Kong Electronics Ltd',
          businessEmail: 'billing@hkelectronics.com',
          customerName: `Customer ${i + 1}`,
          customerEmail: `customer${i + 1}@example.com`,
          customerCountry,
          amount: Math.floor(Math.random() * 50000) + 1000,
          currency,
          description: `Professional services for ${invoiceNumber}`,
          dueDate: dueDate.toISOString().split('T')[0],
          status,
          settlementRail,
          paymentLink: `https://pay.stablepay.com/invoice/${id}`,
          qrCode: `data:image/svg+xml;base64,${btoa(`<svg>QR Code for ${id}</svg>`)}`,
          createdAt: createdDate.toISOString(),
          sentAt: status !== 'draft' ? createdDate.toISOString() : undefined,
          paidAt: status === 'paid' ? new Date(createdDate.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString() : undefined
        })
      }
      
      // Apply filters
      let filteredInvoices = mockInvoices
      if (filters?.status) {
        filteredInvoices = mockInvoices.filter(invoice => invoice.status === filters.status)
      }
      
      // Apply pagination
      const page = filters?.page || 1
      const limit = filters?.limit || 10
      const startIndex = (page - 1) * limit
      const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + limit)
      
      return {
        invoices: paginatedInvoices,
        total: filteredInvoices.length,
        page,
        limit
      }
    }, 'getInvoices')
  }

  async getInvoice(invoiceId: string): Promise<MockApiResponse<MockInvoice | null>> {
    return this.makeApiCall(() => {
      // For demo purposes, generate a mock invoice based on the ID
      const createdDate = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
      const dueDate = new Date(createdDate.getTime() + 14 * 24 * 60 * 60 * 1000)
      
      return {
        id: invoiceId,
        invoiceNumber: `INV-${Math.random().toString(36).substr(2, 4).toUpperCase()}`,
        businessName: 'Hong Kong Electronics Ltd',
        businessEmail: 'billing@hkelectronics.com',
        customerName: 'International Customer',
        customerEmail: 'customer@example.com',
        customerCountry: 'United States',
        amount: 25000,
        currency: 'HKD' as const,
        description: 'Professional consulting services for Q4 2024',
        dueDate: dueDate.toISOString().split('T')[0],
        status: 'sent' as const,
        settlementRail: 'FPS' as const,
        paymentLink: `https://pay.stablepay.com/invoice/${invoiceId}`,
        qrCode: `data:image/svg+xml;base64,${btoa(`<svg>QR Code for ${invoiceId}</svg>`)}`,
        createdAt: createdDate.toISOString(),
        sentAt: createdDate.toISOString()
      }
    }, 'getInvoice')
  }

  async createInvoice(invoiceData: MockInvoiceFormData): Promise<MockApiResponse<MockInvoice>> {
    return this.makeApiCall(() => {
      const id = `invoice_${Math.random().toString(36).substr(2, 9)}`
      const invoiceNumber = `INV-${Math.random().toString(36).substr(2, 4).toUpperCase()}`
      const paymentLink = `https://pay.stablepay.com/invoice/${id}`
      const qrCode = `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="white"/><text x="100" y="100" text-anchor="middle" font-family="Arial" font-size="12" fill="black">QR Code</text><text x="100" y="120" text-anchor="middle" font-family="Arial" font-size="10" fill="gray">${invoiceNumber}</text></svg>`)}`
      
      const invoice: MockInvoice = {
        id,
        invoiceNumber,
        businessName: 'Hong Kong Electronics Ltd',
        businessEmail: 'billing@hkelectronics.com',
        customerName: invoiceData.customerName,
        customerEmail: invoiceData.customerEmail,
        customerCountry: invoiceData.customerCountry,
        amount: invoiceData.amount,
        currency: invoiceData.currency,
        description: invoiceData.description,
        dueDate: invoiceData.dueDate,
        status: 'draft',
        settlementRail: invoiceData.settlementRail,
        paymentLink,
        qrCode,
        createdAt: new Date().toISOString()
      }
      
      return invoice
    }, 'createInvoice')
  }

  async sendInvoice(invoiceId: string): Promise<MockApiResponse<MockInvoice>> {
    return this.makeApiCall(() => {
      // Simulate sending invoice
      const invoice = {
        id: invoiceId,
        status: 'sent' as const,
        sentAt: new Date().toISOString()
      }
      
      return invoice as MockInvoice
    }, 'sendInvoice')
  }

  async generatePaymentLink(invoiceId: string): Promise<MockApiResponse<{ paymentLink: string; qrCode: string }>> {
    return this.makeApiCall(() => {
      const paymentLink = `https://pay.stablepay.com/invoice/${invoiceId}`
      const qrCode = `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="white"/><rect x="20" y="20" width="160" height="160" fill="none" stroke="black" stroke-width="2"/><rect x="40" y="40" width="20" height="20" fill="black"/><rect x="80" y="40" width="20" height="20" fill="black"/><rect x="120" y="40" width="20" height="20" fill="black"/><rect x="160" y="40" width="20" height="20" fill="black"/><rect x="40" y="80" width="20" height="20" fill="black"/><rect x="120" y="80" width="20" height="20" fill="black"/><rect x="40" y="120" width="20" height="20" fill="black"/><rect x="80" y="120" width="20" height="20" fill="black"/><rect x="160" y="120" width="20" height="20" fill="black"/><rect x="40" y="160" width="20" height="20" fill="black"/><rect x="80" y="160" width="20" height="20" fill="black"/><rect x="120" y="160" width="20" height="20" fill="black"/><rect x="160" y="160" width="20" height="20" fill="black"/></svg>`)}`
      
      return {
        paymentLink,
        qrCode
      }
    }, 'generatePaymentLink')
  }

  // Configuration methods
  updateConfig(newConfig: Partial<MockApiConfig>): void {
    this.config = { ...this.config, ...newConfig }
  }

  getConfig(): MockApiConfig {
    return { ...this.config }
  }

  // Reset data for demo purposes
  resetData(): void {
    this.paymentStorage.clear()
    this.settlementStorage.clear()
    this.initializeData()
  }
}

// Export singleton instance
export const mockApiService = new MockApiService()